import { format } from 'date-fns';
import saveAs from 'file-saver';
import { ReactNode, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '../../../apps/Home/components/TemporalBanner';
import { s3dirname } from '../../../apps/Storage/components/StorageMy';
import { ReactComponent as TV } from '../../../assets/img/icons/tv.svg';
import { ReactComponent as FileDoc } from '../../../assets/svg/file-doc.svg';
import { ReactComponent as Filter } from '../../../assets/svg/filter.svg';
import { ACCOUNT_TYPES, ORDER_STATUSES } from '../../../const';
import { useAuth } from '../../../hooks';
import NoCover from '../../../img/no-cover.svg';
import { extractAccount, extractPrefix } from '../../../pages/ContentEditorPage';
import { useGetXlsMutation } from '../../../services/api';
import {
    AccessFull,
    AccessRequestType,
    AccessResponse,
    ContentType,
    LicenseAgreement,
    useAccessRequestsSetStatusMutation,
    useGetAccessRequestsAccountsQuery,
    useGetMediaHeaderMutation,
    useGetMetaDataMutation,
    useLazyGetAccessRequestQuery,
    useLazyGetAccessRequestsQuery,
    useLazyGetContentUnitMediaQuery
} from '../../../services/content_v1';
import { BASE_URL } from '../../../services/reauth';
import { AccessRequestStatus, AccountResType } from '../../../services/users_v1';
import { OuterMenuProps } from '../../../types/index';
import { useAlert } from '../../alert';
import { extractErrorDetail, toMplatDate, useIsAdmin, useIsConsumer, useIsMedialogistic, useIsPartnerSource } from '../../common';
import Button from '../Button/Button';
import { CheckBox } from '../CheckBox';
import Table from '../DataTable/Table';
import TableBody from '../DataTable/TableBody';
import { TableColumn } from '../DataTable/TableColumn';
import TableHeader from '../DataTable/TableHeader';
import { TableHeaderColumn } from '../DataTable/TableHeaderColumn';
import TableListName from '../DataTable/TableListName';
import TableRow from '../DataTable/TableRow';
import { DatePicker } from '../DatePicker';
import { AVAIL_SOURCES } from '../Deals/RegistryContent';
import ImageLoader from '../ImageLoader';
import MainContentFilter from '../MainContentFilter';
import { MobileDateFilterItem } from '../MobileDateFilterItem';
import MobileFilter from '../MobileFilter';
import { MobileFilterItem } from '../MobileFilterItem';
import { Modal } from '../Modal';
import { CommentModalDialog } from '../Modals/CommentModal';
import { ErrorModal } from '../Modals/ErrorModal';
import { PermissionViewModal } from '../Modals/PermissionViewModal';
import MultiSelect from '../MultiSelect';
import NoHideEmailAdmin from '../NoHide/NoHideEmailAdmin';
import NoHideFIOAdmin from '../NoHide/NoHideFIOAdmin';
import NoHidePhoneAdmin from '../NoHide/NoHidePhoneAdmin';
import { Searcher } from '../Searcher';
import { SelectButton } from '../SelectButtonSpan';
import { Spinner } from '../Spinner';
import { OrderProviderReplyModal } from './DealContentProvider';


function getOrderDate(order: AccessResponse) {
    if (order.added) {
        const timeStr = order.added;
        const dt = Date.parse(timeStr);
        // 14.05.2022 · 18:43
        return format(dt, 'dd.MM.yyyy · HH:mm');
    } else
        return '';
}

export function getPrefix(prefixes?: string[]) {
    if (prefixes?.length) {
        const firstPrefix = prefixes[0];
        if (firstPrefix.endsWith('/')) {
            return extractAccount(firstPrefix) + '/' + encodeURIComponent(extractPrefix(firstPrefix));
        } else {
            const folderPrefix = s3dirname(firstPrefix);
            return extractAccount(folderPrefix) + '/' + encodeURIComponent(extractPrefix(folderPrefix));
        }
    }
    return '';
}

export function toSourceParams(rawParams: string | undefined): ReactNode {
    if (rawParams) {
        const params = rawParams?.split('|') || [''];
        if (params.length > 1) {
            return (<>
                {params[0]}
                <br />
                {params[1]}
            </>);
        } else {
            return params[0];
        }
    }
    return '';
}

export interface OrdersTableRowProps {
    order: AccessResponse,
    onShowPermission: (id: number) => void
    onShowOrder: (id: number, accId: number) => void
    onCreatePermission: (id: number, acc_id: number) => void
}

function OrdersTableRow({ order, onShowPermission, onShowOrder, onCreatePermission }: OrdersTableRowProps) {
    const alert = useAlert();
    const isConsumer = useIsConsumer();
    const isAdmin = useIsAdmin();
    const isPartnerSource = useIsPartnerSource();
    const isMdlg = useIsMedialogistic();
    const isMobile = useMediaQuery('(max-width: 760px)');

    const [mediafilesRequest] = useLazyGetContentUnitMediaQuery();
    const [getMetaData] = useGetMetaDataMutation();
    const [setStatus] = useAccessRequestsSetStatusMutation();
    const [getHeader] = useGetMediaHeaderMutation();

    const firstUnit = order.units.at(0);
    const coverPath = firstUnit && firstUnit.covers.length > 0 ? firstUnit.covers[0] : '';
    const disabled = order.status === AccessRequestStatus.REJECTED || order.status === AccessRequestStatus.CANCELED;
    const { t } = useTranslation();
    const [error, setError] = useState<string>('');
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [showComment, setShowComment] = useState(false);

    function onDownload(unitId: number, title: string) {
        mediafilesRequest(Number(unitId)).unwrap().then((data) => {
            getMetaData({ id: unitId as number }).unwrap()
                .then((metaData) => {
                    const filename = title;
                    saveAs(metaData, `${filename}.zip`);
                })
                .catch(e => {
                    setErrorTitle(t('Metadata download error'));
                    setError(extractErrorDetail(e));
                })

            if (!!data.results.mediafiles?.length) {
                data.results.mediafiles?.forEach(f => {
                    getHeader(BASE_URL.substring(0, BASE_URL.length - 1) + f.url).then((d) => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if ((d as any).error?.status === 418) {
                            alert.error(t('content:2FA_ERROR'));
                        } else {
                            window.open(BASE_URL.substring(0, BASE_URL.length - 1) + f.url);
                        }
                    }).catch((err) => {
                        if (err.status === 418) {
                            alert.error(t('content:2FA_ERROR'));
                        } else {
                            alert.error(extractErrorDetail(err));
                        }
                    });
                });
            }
        }).catch((e) => {
            setErrorTitle(t('Get mediafiles error'));
            setError(extractErrorDetail(e));
        });
    }

    return (
        <TableRow key={order.id} className={`${disabled ? '_status-set' : ''}`}>
            <CommentModalDialog
                isOpen={showComment}
                onClose={() => setShowComment(false)}
                comment={order.comment || ''}
                action={(c) => setStatus({ ar_id: order.id, acc_id: order.account_id, comment: c })}
            />
            <ErrorModal isOpen={!!error} onClose={() => setError('')} content={error} title={errorTitle} />

            <TableColumn className="_id">{order.id}</TableColumn>
            <TableColumn className="_status">
                <span className="orders-list__status-label">{t(ORDER_STATUSES[order.status])}</span>
                {/* TODO использовать если понадобится */}
                {/* <div className="orders-list__status-cancel">
                    <a href="#" className="btn _min _bd-accent">Отменить</a>
                </div> */}
            </TableColumn>
            {isAdmin ?
                <TableColumn className="_producer">
                    <div className="orders-list__subscriber">
                        {!isMobile && order.recipient?.logo &&
                            <ImageLoader url={order.recipient?.logo} className="orders-list__subscriber-logo" />
                        }
                        <strong className="orders-list__subscriber-name">
                            {order.is_favorites &&
                                <span className="catalog-card__controls-status _min _vip"
                                    title={t('account:VIP_ACCESS')}>VIP
                                </span>
                            }
                            <Link className="link-accent" to={`/accounts/view/${order.recipient.id}`}>{order.recipient?.name}</Link> · {order.recipient?.org.name}
                        </strong>

                        <span className="orders-list__subscriber-rep">
                            <NoHideFIOAdmin user={order.recipient?.owner} />
                        </span>
                        {' · '}
                        <NoHidePhoneAdmin user={order.recipient?.owner} />
                        {' · '}
                        <NoHideEmailAdmin user={order.recipient?.owner} />
                    </div>
                </TableColumn>
                :
                <TableColumn className="_subscriber">
                    <div className="orders-list__subscriber">
                        {!isMobile && order.provider.logo &&
                            <ImageLoader url={order.provider.logo} className="orders-list__subscriber-logo" />
                        }
                        <strong className="orders-list__subscriber-name">
                            {order.is_favorites &&
                                <span className="catalog-card__controls-status _min _vip"
                                    title={t('account:VIP_ACCESS')}>VIP
                                </span>
                            }
                            <Link className="link-accent" to={`/accounts/view/${order.provider.id}`}>{order.provider.name}</Link> · {order.provider?.org.name}
                        </strong>

                        <span className="orders-list__subscriber-rep">
                            <NoHideFIOAdmin user={order.provider.owner} />
                        </span>
                        {' · '}
                        <NoHidePhoneAdmin user={order.provider.owner} />
                        {' · '}
                        <NoHideEmailAdmin user={order.provider.owner} />
                        {/* TODO реальный договор  */}
                        {/* <div className="orders-list__subscriber-contract">
                            {order.contracts.map((c) => <Link key={c} to={`/deals/registry?contract=${c}`} className="link-accent">
                                <Trans>Contract</Trans> <DlArrow />
                            </Link>)}
                        </div> */}
                    </div>
                </TableColumn>
            }
            {(isAdmin || isPartnerSource) && <TableColumn className="_subscriber">
                <div className="orders-list__subscriber">
                    {order.consumer.logo &&
                        <ImageLoader url={order.consumer.logo} className="orders-list__subscriber-logo" />
                    }
                    <strong className="orders-list__subscriber-name">
                        {order.is_favorites &&
                            <span className="catalog-card__controls-status _min _vip"
                                title={t('account:VIP_ACCESS')}>VIP
                            </span>
                        }
                        <Link className="link-accent" to={`/accounts/view/${order.consumer.id}`}>{order.consumer.name}</Link> · {order.consumer?.org.name}
                    </strong>

                    <span className="orders-list__subscriber-rep">
                        <NoHideFIOAdmin user={order.consumer.owner} />
                    </span>
                    {' · '}
                    <NoHidePhoneAdmin user={order.consumer.owner} />
                    {' · '}
                    <NoHideEmailAdmin user={order.consumer.owner} />
                    {/* TODO договор */}
                    {/* <div className="orders-list__subscriber-contract">
                        <Link to="#" className="link-accent">
                            <Trans>Contract</Trans> <DlArrow />
                        </Link>
                    </div> */}
                </div>
            </TableColumn>}
            <TableColumn className="_content">
                {order.artype === AccessRequestType.MEDIA ?
                    <TableListName
                        noimg
                        url={coverPath}
                        link={isConsumer ? `/content/view/${order.units.at(0)?.id || NaN}` : `/content/media/${order.units.at(0)?.id || NaN}/`}
                        title={order.units.at(0)?.title}
                    />
                    : (order.artype === AccessRequestType.PRODUCT ?
                        <TableListName
                            noimg
                            url={coverPath}
                            link={isConsumer ? `/content/service/view/${order.products.at(0)?.id || NaN}` : `/content/service/${order.products.at(0)?.id || NaN}/`}
                            title={order.products.at(0)?.name}
                        />
                        :
                        <div className={`orders__content ${isConsumer && '_user'}`}>
                            <span className="orders__content-tv link link-accent" onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onShowOrder(order.id, order.account_id);
                            }} style={{ stroke: 'black' }}>
                                <TV />&nbsp;<Trans>order:TV_COUNT</Trans>: {order.units.length}
                            </span>
                        </div>)
                }
            </TableColumn>
            {!isAdmin &&
                <TableColumn className="_comment">
                    {order.comment ? <>{order.comment}<br /></> : ''}
                    {order.comment ?
                        <span className="link link-accent" onClick={() => setShowComment(true)}><Trans>Edit</Trans><span className="__mobile"> <Trans>comment</Trans></span></span>
                        :
                        <span className="link link-accent _comment-add" onClick={() => setShowComment(true)}><Trans>Add</Trans><span className="__mobile"> <Trans>comment</Trans></span></span>
                    }
                </TableColumn>
            }
            <TableColumn className="_agreement">
                <span className="orders__content-status">
                    <span className="orders__content-status">
                        {[AccessRequestStatus.GRANTED].includes(order.status) && order.permissions.at(0)?.id &&
                            <span className="link link-accent" onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onShowPermission(order.permissions.at(0)?.id || NaN);
                            }}>
                                <Trans>Permission</Trans>
                            </span>
                        }
                    </span>
                </span>
                {isAdmin ?
                    <div className="orders-list__subscriber-cta">
                        {order.status === AccessRequestStatus.GRANTED && order.units.at(0)?.ctype === ContentType.UNIT &&
                            <Link to={`/sdn/avail/${getPrefix(order.prefixes)}`} className="btn _min" onClick={() => { return; onDownload(order.units.at(0)?.id || NaN, order.units.at(0)?.title || '') }}>
                                <Trans>Download</Trans>
                            </Link>
                        }
                        {[AccessRequestStatus.OPEN, AccessRequestStatus.PENDING].includes(order.status) && order.recipient?.owner.email &&
                            <a href={`mailto:${order.recipient?.owner.email}`}
                                className="btn _min _bd">{t('Orders operation write')}</a>
                        }
                    </div>
                    :
                    <>
                        <div className="orders-list__subscriber-cta">
                            {order.status === AccessRequestStatus.GRANTED && order.units.at(0)?.ctype === ContentType.UNIT &&
                                <Link to={`/sdn/avail/${getPrefix(order.prefixes)}`} className="btn _min" onClick={() => { return; onDownload(order.units.at(0)?.id || NaN, order.units.at(0)?.title || '') }}>
                                    <Trans>Download</Trans>
                                </Link>
                            }
                        </div>
                        <div className="orders-list__subscriber-cta">
                            {[AccessRequestStatus.OPEN, AccessRequestStatus.PENDING].includes(order.status) && order.provider.owner.email &&
                                <a href={`mailto:${order.provider.owner.email}`}
                                    className="btn _min _bd">{t('Orders operation write')}</a>
                            }
                        </div>
                        {isMdlg && [AccessRequestStatus.OPEN, AccessRequestStatus.PENDING].includes(order.status) &&
                            <div className="orders-list__subscriber-cta">
                                <Button size="small" className="_bd" onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onCreatePermission(order.id, order.account_id);
                                }}>
                                    {t('Orders operation reply')}
                                </Button>
                            </div>
                        }
                        {!isMdlg && (
                            <div className="orders-list__subscriber-cta">
                                {[AccessRequestStatus.OPEN, AccessRequestStatus.PENDING].includes(order.status) && order.provider.owner.email &&
                                    <Button size="small" className="_bd-red" onClick={() => {
                                        // eslint-disable-next-line no-restricted-globals
                                        const ret = confirm(t('order:DELETE_CONFIRM_MESSAGE'));
                                        if (ret) {
                                            setStatus({ ar_id: order.id, acc_id: order.account_id, status: AccessRequestStatus.CANCELED }).unwrap().then(() => {
                                                console.log('Перевод в статус Отменена');
                                            }).catch((err) => {
                                                alert.error(extractErrorDetail(err));
                                            });
                                        }
                                    }}>
                                        <Trans>Cancel</Trans>
                                    </Button>
                                }
                            </div>
                        )}
                    </>
                }
            </TableColumn>
            <TableColumn className="_date">{getOrderDate(order)} <span className="_mobile"> · ID {order.id}</span></TableColumn>
        </TableRow >
    )
}

interface OrderModalProps {
    isOpen: boolean,
    orderId: number,
    accId: number,
    onClose: () => void,
}

function OrderViewModal({ isOpen, orderId, accId, onClose }: OrderModalProps) {
    const { t } = useTranslation();
    const auth = useAuth();
    const [refetch, { data: accessData }] = useLazyGetAccessRequestQuery();

    const [access, setAccess] = useState<AccessFull | undefined>(undefined);

    useEffect(() => {
        if (!isNaN(orderId)) {
            refetch({ ar_id: orderId, acc_id: accId || auth.account });
        }
    }, [auth, orderId]);

    useEffect(() => {
        if (accessData?.results) {
            setAccess(accessData?.results);
        }
    }, [accessData]);

    return (
        <Modal className={'order__permission-popup'} isOpen={isOpen} onClose={() => onClose()}>
            {access && (
                <div className="order__permission">
                    <div className="user-settings__labels">
                        <span className="user-settings__labels-item">ID · {orderId}</span>
                        <span className="user-settings__labels-item">{toMplatDate(access.added, true)}</span>
                    </div>
                    <h2 className="popup__heading"><Trans>order:ORDER_REQ</Trans>:</h2>
                    <div className="tv-content__order-list">
                        {access.units.map((unit) => <div key={unit.id} className="tv-content__order-list-item">
                            {unit.covers.length > 0 ? <ImageLoader url={unit.covers[0]} /> : <img src={NoCover} alt="" />}
                            <span className="tv-content__order-list-item-name">{unit.title}</span>
                            <span className="tv-content__order-list-item-theme">{unit.category?.name}</span>
                        </div>)}
                    </div>

                    <h3><Trans>order:HAVE_RIGHTS</Trans></h3>
                    <p>
                        {access.details.licagr === LicenseAgreement.NO && t('No')}
                        {access.details.licagr === LicenseAgreement.YES && t('Yes')}
                        {access.details.licagr === LicenseAgreement.PARTIAL && t('order:ORDER_LIC_PARTIAL')}
                    </p>

                    <h3><Trans>order:ORDER_REQ_WHO</Trans></h3>
                    <p><Trans>{ACCOUNT_TYPES[access.owner.rtype || 0]}</Trans></p>
                    <p>
                        <Link to={`/accounts/view/${access.person.id}`} className="link-accent">
                            <NoHideFIOAdmin user={access.person} />
                        </Link>, <br />
                        <NoHidePhoneAdmin user={access.person} />,
                        <br />
                        <NoHideEmailAdmin user={access.person} />
                    </p>

                    <h3>
                        <Trans>
                            order:ORDER_ADMIN
                        </Trans>
                    </h3>
                    <p>
                        <Link to={`/accounts/view/${access.provider.id}`} className="link-accent">
                            <NoHideFIOAdmin user={access.provider.owner} />
                        </Link>, <br />
                        <NoHidePhoneAdmin user={access.provider.owner} />,
                        <br />
                        <NoHideEmailAdmin user={access.provider.owner} />
                    </p>

                    {access.details.promocodes.length > 0 && <>
                        <h3>
                            <Trans>order:ORDER_PROMO</Trans>
                        </h3>
                        <p>
                            <span className="user-settings__labels-item">{access.details.promocodes.join(', ')}</span>
                        </p>
                    </>}

                    <h3>
                        <Trans>order:ORDER_WISHES</Trans>
                    </h3>
                    <p>
                        <span className="user-settings__labels-item">{AVAIL_SOURCES.find((s) => s.value === access.details.pstype)?.label}</span>
                        {' '}
                        {access.details.psdetails && <span className="user-settings__labels-item">{access.details.psdetails}</span>}
                    </p>

                    <h3><Trans>order:REQ_TEST_PERIOD</Trans></h3>
                    <p>{access.details.ontest ? t('Yes') : t('No')}</p>
                </div>
            )}
        </Modal >
    )
}

function DealContentConsumer({ menu }: OuterMenuProps) {
    // const
    const PAGE_SIZE = 24;
    // hooks
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const auth = useAuth();
    const isAdmin = useIsAdmin();
    const isPartnerSource = useIsPartnerSource();
    // fetches
    const [trigger, { data: xlsData }] = useGetXlsMutation();
    const [refetch, { data }] = useLazyGetAccessRequestsQuery();
    const { data: clients } = useGetAccessRequestsAccountsQuery();
    // states
    const [current, setCurrent] = useState(0);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(false);

    const [permIdDialog, setPermIdDialog] = useState(NaN);
    const [orderIdDialog, setOrderIdDialog] = useState(NaN);
    const [accIdDialog, setAccIdDialog] = useState(NaN);

    const [extPermIdDialog, setExtPermIdDialog] = useState(NaN);
    const [accId, setAccId] = useState(NaN);

    const [search, setSearch] = useState<string>('');
    const deferredSearch = useDeferredValue(search);
    const [date, setDate] = useState<Date | undefined>();
    const [companyFilter, setCompanyFilter] = useState<string[]>([]);
    const [ctypeFilter, setCTypeFilter] = useState<number[]>([]);
    const [cTypeTVFilter, setCTypeTVFilter] = useState<boolean>(false);
    const [cTypeContentFilter, setCTypeContentFilter] = useState<boolean>(false);

    const restoredMainFilter = sessionStorage.dealOrdersFilter
        ? (sessionStorage.dealOrdersFilter !== 'all' ? Number(sessionStorage.dealOrdersFilter) : 'all')
        : AccessRequestStatus.ACTIVE;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [mainFilter, setMainFilter] = useState<any>(restoredMainFilter);
    const [byId, setById] = useState('');
    const [ordering, setOrdering] = useState('-id');

    const [filterVisible, setFilterVisible] = useState(false);
    const [filterCount, setFilterCount] = useState<number>(0);

    const mainFilterItems = useMemo(() => [
        { value: 'all', label: t('Orders filter all'), counter: data?.results.totals['all'] },
        { value: AccessRequestStatus.ACTIVE, label: t('Active'), counter: (data?.results.totals['opened'] || 0) + (data?.results.totals['pending'] || 0) },
        { value: AccessRequestStatus.OPEN, label: t('Orders filter new'), counter: data?.results.totals['opened'] },
        { value: AccessRequestStatus.PENDING, label: t('Orders filter pending'), counter: data?.results.totals['pending'] },
        { value: AccessRequestStatus.GRANTED, label: t('Orders filter granted'), counter: data?.results.totals['granted'] },
        { value: AccessRequestStatus.REJECTED, label: t('Orders filter rejected'), counter: data?.results.totals['rejected'] },
        { value: AccessRequestStatus.ARCHIVED, label: t('Orders filter archived'), counter: (data?.results.totals['rejected'] || 0) + (data?.results.totals['granted'] || 0) },
    ], [data?.results, t]);

    const [companyList, setCompanyList] = useState<{
        value: string;
        label: string;
    }[]>([]);

    const onClean = useCallback(() => {
        setSearch('');
        setDate(undefined);
        setCompanyFilter([]);
        setCTypeFilter([]);
        setCTypeTVFilter(false);
        setCTypeContentFilter(false);
    }, []);

    // effects
    useEffect(() => {
        sessionStorage.dealOrdersFilter = mainFilter;
        setCurrent(0);
    }, [companyFilter, date, mainFilter, ordering]);

    useEffect(() => {
        const tmp: number[] = [];
        if (cTypeContentFilter) tmp.push(ContentType.UNIT);
        if (cTypeTVFilter) tmp.push(ContentType.TV);
        setCTypeFilter(tmp);
    }, [cTypeTVFilter, cTypeContentFilter]);

    useEffect(() => {
        setIsFetched(true);
        const sender = companyFilter.join(',');
        const effectiveStatus = mainFilter === 'all' ? '' : (mainFilter === AccessRequestStatus.ARCHIVED ? '2&status=1' : (mainFilter === AccessRequestStatus.ACTIVE ? '0&status=3' : mainFilter));
        refetch({
            offset: current,
            limit: PAGE_SIZE,
            ordering,
            id: byId,
            search: deferredSearch,
            artype: ctypeFilter.join(','),
            provider_name: sender,
            status: effectiveStatus,
            crdate: (date && `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`) || '',
            u: auth.user + auth.account,
        })
            .then(() => setIsFetched(false))
            .catch(() => setIsFetched(false));
    }, [current, date, deferredSearch, ordering, refetch, companyFilter, ctypeFilter, mainFilter, auth.account, auth.user]);

    useEffect(() => {
        setHasMore(!!data?.results.next);
    }, [data?.results]);

    const handleNavigation = useCallback(() => {
        if ((document.body.scrollHeight - window.innerHeight) - window.scrollY < 300 && !isFetched) {
            if (hasMore) {
                setCurrent(data?.results.data.length || 0);
            }
        }
    }, [data?.results.data.length, hasMore, isFetched]);

    useEffect(() => {
        window.addEventListener('scroll', handleNavigation);

        return () => {
            window.removeEventListener('scroll', handleNavigation);
        };
    }, [handleNavigation]);

    useEffect(() => {
        const tmp: Record<string, string> = {};
        searchParams.forEach((v, k) => {
            tmp[k] = v;
        });
        if (tmp.status) {
            setMainFilter(tmp.status === 'all' ? 'all' : Number(tmp.status));
        }
        if (tmp.id) {
            setById(tmp.id);
            setMainFilter('all');
        }
    }, [searchParams]);

    useEffect(() => {
        if (xlsData) {
            saveAs(xlsData, 'orderlist.xls');
        }
    }, [xlsData]);

    useEffect(() => {
        const uniqueCompany = new Set<string>();
        clients?.results.filter((c) => c.rtype === AccountResType.PROVIDER).every(s => uniqueCompany.add(s.name));
        const result: { value: string, label: string }[] = [];
        uniqueCompany.forEach((c) => result.push({ value: c, label: c }));
        setCompanyList(result);
    }, [clients]);

    useEffect(() => {
        setFilterCount(
            + (date ? 1 : 0)
            + companyFilter.length
            + ctypeFilter.length
        )
    }, [date, companyFilter.length, ctypeFilter.length]);

    return (<>
        <OrderViewModal isOpen={!isNaN(orderIdDialog)} orderId={orderIdDialog} accId={accIdDialog} onClose={() => { setOrderIdDialog(NaN); setAccIdDialog(NaN); }} />
        <PermissionViewModal isOpen={!isNaN(permIdDialog)} permId={permIdDialog} onClose={() => setPermIdDialog(NaN)} />
        <OrderProviderReplyModal isOpen={!isNaN(extPermIdDialog)} orderId={extPermIdDialog} accId={accId} onClose={() => setExtPermIdDialog(NaN)} />

        <MainContentFilter>
            {menu}

            <SelectButton sub value={mainFilter} items={mainFilterItems} onChange={(v) => { setById(''); setMainFilter(v); }}>
                <a href="#xls" className="main__types-file" target="_blank" onClick={(e) => {
                    e.preventDefault();
                    trigger('/be/api/content/access/requests/xlsx/');
                }}>
                    <FileDoc />
                    <Trans>Orders xls export</Trans>
                </a>
            </SelectButton>

            <div className="main__controls _no-sort">
                <div className="main__controls-filter">
                    <Searcher value={search} onChange={(v) => setSearch(v)} />
                    <div className="main__controls-filter-item">
                        <DatePicker value={date} placeholder={t('Orders filter date')} setValue={(v) => setDate(v)} />
                    </div>
                    <MultiSelect className="main__controls-filter-item _wide _pre-checkbox" placeholder={t('Orders filter producer')}
                        value={companyFilter} options={companyList || []} setValue={(v) => setCompanyFilter(v)} minimized />
                    {/* <MultiSelect className="main__controls-filter-item" placeholder={'Тип контента'} */}
                    {/* value={ctypeFilter} options={availCTypes} setValue={(v) => setCTypeFilter(v)} minimized /> */}
                    <div className="main__controls-filter-item _pre-checkbox">
                        <CheckBox value={cTypeTVFilter} label={t('account:SPEC_TV')} onChange={(v) => setCTypeTVFilter(v)} />
                    </div>
                    <div className="main__controls-filter-item">
                        <CheckBox value={cTypeContentFilter} label={t('account:SPEC_CONTENT')} onChange={(v) => setCTypeContentFilter(v)} />
                    </div>

                    {(filterCount > 0 || search) && <div className="main__controls-filter-item">
                        <Button className="_bd" size="small" title={t('Clean')} onClick={onClean} style={{ lineHeight: '24px' }}>
                            🧹
                        </Button>
                    </div>}
                </div>
                <div className="main__controls-filter-trigger" onClick={() => setFilterVisible(!filterVisible)}>
                    <Filter />
                    {!!filterCount && <span className="main__controls-filter-trigger-counter">{filterCount}</span>}
                </div>
            </div>
        </MainContentFilter>

        <Table className="orders-list">
            <TableHeader>
                <TableHeaderColumn header="ID" field="id" className="_id" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={t('Orders table status')} field="status" className="_status" ordering={ordering} setOrdering={setOrdering} />
                <TableHeaderColumn header={t('Orders table producer')} field="provider_name" className={isAdmin ? '_provider' : '_subscriber'} ordering={ordering} setOrdering={setOrdering} />
                {(isAdmin || isPartnerSource) && <TableHeaderColumn header={t('Orders table consumer')} field="consumer_name" className="_subscriber" ordering={ordering} setOrdering={setOrdering} />}
                <TableHeaderColumn header={t('Product')} field="title" className="_content" ordering={ordering} setOrdering={setOrdering} />
                {!isAdmin && <TableHeaderColumn header={t('Comment')} field="comment" className="_comment" ordering={ordering} setOrdering={setOrdering} />}
                <TableHeaderColumn header={''} className="_agreement" />
                <TableHeaderColumn header={t('Orders table date')} field="added" className="_date" ordering={ordering} setOrdering={setOrdering} />
            </TableHeader>
            <TableBody>
                {data?.results.data.map(order => (
                    <OrdersTableRow
                        key={`${order.id}+${order.account_id}`}
                        order={order}
                        onShowPermission={(permId) => setPermIdDialog(permId)}
                        onShowOrder={(orderId, aId) => { setOrderIdDialog(orderId); setAccIdDialog(aId); }}
                        onCreatePermission={(pId, aId) => { setExtPermIdDialog(pId); setAccId(aId); }}
                    />
                ))}
            </TableBody>
            {isFetched && <Spinner />}
        </Table>

        <MobileFilter
            filterVisible={filterVisible}
            setFilterVisible={setFilterVisible}
            onClean={onClean}
        >
            <MobileDateFilterItem value={date} label={t('Orders filter date')}
                placeholder={t('Orders filter date placeholder')}
                setValue={(v) => setDate(v)} />
            <MobileFilterItem label={t('Orders filter producer')}
                value={companyFilter} options={companyList || []} setValue={(v) => setCompanyFilter(v)} />
            <MobileFilterItem label={t('Type')}
                value={ctypeFilter}
                options={[{ value: 1, label: t('account:SPEC_CONTENT') }, { value: 2, label: t('account:SPEC_TV') }]}
                setValue={(v) => {
                    setCTypeTVFilter(v.includes(2));
                    setCTypeContentFilter(v.includes(1))
                }}
            />
        </MobileFilter>

    </>)
}

export default DealContentConsumer;
